import { DateTime } from "luxon";

/**
 * Reading a time the way somebody types it.
 *
 * The editor used to offer ninety-six quarter-hour options in a dropdown, which
 * is fine until a haircut is at 2:40 — and then the time simply is not in the
 * list. A text field solves that, but only if it accepts what people actually
 * write rather than one blessed spelling. So all of these are the same time:
 *
 *   "2:40pm"  "2:40 PM"  "240p"  "1440"  "14:40"  "2.40 pm"  "2 40 pm"
 *
 * The rule for a bare hour is the interesting one. "2" almost certainly means
 * two in the afternoon, and "8" almost certainly means eight in the morning, so
 * 1 through 6 are read as afternoon and everything else literally. That is a
 * guess, but a well-founded one: across four months of this household's real
 * calendar, not one event started before seven in the morning. Typing "2a" is
 * always available for the exception.
 */

const MERIDIEM = /\s*([ap])\.?\s*m?\.?$/;

/**
 * @returns "HH:mm" in 24-hour form, or null if it can't be read at all.
 */
export function parseTime(input: string): string | null {
  const cleaned = input.trim().toLowerCase();
  if (cleaned === "") return null;

  // Pull the am/pm off the end first, so the rest is only digits and separators.
  const meridiemMatch = MERIDIEM.exec(cleaned);
  const meridiem = meridiemMatch?.[1] ?? null;
  const rest = (meridiemMatch === null ? cleaned : cleaned.slice(0, meridiemMatch.index)).trim();

  const digits = rest.replace(/[:.\s]/g, "");
  if (!/^\d{1,4}$/.test(digits)) return null;

  // Whether the typist wrote the minutes out, which decides how a bare number
  // is read: "1440" is a 24-hour clock, "2" is an hour on its own.
  const separated = /[:.\s]/.test(rest);

  let hour: number;
  let minute: number;

  if (separated) {
    const [left, right = "0"] = rest.split(/[:.\s]+/);
    hour = Number(left);
    minute = Number(right.padEnd(2, "0").slice(0, 2));
  } else if (digits.length <= 2) {
    hour = Number(digits);
    minute = 0;
  } else {
    hour = Number(digits.slice(0, digits.length - 2));
    minute = Number(digits.slice(-2));
  }

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (minute > 59) return null;

  if (meridiem !== null) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === "a") hour = hour === 12 ? 0 : hour;
    else hour = hour === 12 ? 12 : hour + 12;
  } else if (digits.length === 4) {
    /*
     * Four digits with no am/pm is the twenty-four hour clock and means exactly
     * what it says — "0130" is half past one in the morning, not the afternoon.
     */
  } else if (hour >= 1 && hour <= 6) {
    hour += 12;
  }

  if (hour > 23) return null;

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** "14:40" → "2:40 pm". The form the rest of Frankie writes times in. */
export function formatTime(value: string): string {
  const [h, m] = value.split(":");
  const time = DateTime.fromObject({ hour: Number(h), minute: Number(m) });
  return time.isValid ? time.toFormat("h:mm a").toLowerCase() : value;
}

/**
 * Moves a time by a number of minutes, wrapping at midnight.
 *
 * For the arrow keys: nudging by five beats retyping, and wrapping means
 * holding Down at 00:00 does something predictable rather than sticking.
 */
export function shiftTime(value: string, byMinutes: number): string {
  const [h, m] = value.split(":");
  const total = (Number(h) * 60 + Number(m) + byMinutes + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * The quarter hours, offered as suggestions rather than as the only choice.
 *
 * The dropdown was right about one thing: most events really are on a quarter
 * hour, and picking beats typing when it fits. These go in a datalist, so they
 * are there when wanted and never in the way.
 */
export function quarterHours(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const value = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
      minutes % 60,
    ).padStart(2, "0")}`;
    out.push({ value, label: formatTime(value) });
  }
  return out;
}
